import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Send, Loader2 } from 'lucide-react';
import { useCampaignStore } from '../store/campaignStore';
import { useEmailTemplateStore } from '../store/emailTemplateStore';
import { campaignsApi, listsApi } from '../lib/api';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { TemplatePreview, CampaignStatusChip, RunDetailModal, AudienceRoleGroups, ImageSetPicker } from '../components/campaigns';
import { summarizeSelector, type BlastRoleKey } from '../lib/campaignRoles';
import type { AttachmentSelector, Campaign, CampaignRun, EmailCampaignStatus, ImageSet } from '../types/campaign';
import type { List } from '../types/list';

type ScheduleMode = 'manual' | 'schedule';
type PendingAction = 'draft' | 'schedule' | 'save' | 'test' | null;

/** Run statuses that mean "still working" — the Runs table polls while any run is in one of these. */
const ACTIVE_RUN_STATUSES: CampaignRun['status'][] = ['QUEUED', 'RUNNING'];

interface ConfirmState {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
}

const EMPTY_SELECTOR: AttachmentSelector = { blastRoles: [], crmContacts: false, donors: false, purchasers: false };
const LOCKED_STATUSES: EmailCampaignStatus[] = ['SENT', 'CANCELED', 'SENDING'];

/** Reads the server's error message off an Axios rejection, falling back to a generic message. */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message;
    if (typeof msg === 'string' && msg) return msg;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** ISO datetime -> the local "YYYY-MM-DDTHH:mm" value a <input type="datetime-local"> expects. */
function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface FormSnapshotInput {
  listId: string;
  selector: AttachmentSelector;
  templateId: string;
  imageSetKey: string;
  scheduleMode: ScheduleMode;
  scheduleAt: string;
}

/**
 * Serializes the audience/template/settings fields Send now and Send test
 * actually act on — those two actions always run against what's persisted
 * on the campaign, never the in-progress form — so comparing this against
 * the same snapshot taken at load/save time detects a form that's diverged
 * from what a send would use.
 */
function formSnapshot(input: FormSnapshotInput): string {
  return JSON.stringify({
    listId: input.listId,
    blastRoles: [...input.selector.blastRoles].sort(),
    crmContacts: input.selector.crmContacts,
    templateId: input.templateId,
    imageSetKey: input.imageSetKey,
    scheduleMode: input.scheduleMode,
    scheduleAt: input.scheduleMode === 'schedule' ? input.scheduleAt : '',
  });
}

/**
 * Guided campaign builder: audience -> template -> settings -> review, one
 * page with `.card` sections plus a run-history card once the campaign is
 * saved. Loads an existing campaign from `campaignsApi.get` when `:id` is
 * present; a blank form at /campaigns/new navigates to /campaigns/<id>
 * after the first save. Routing/nav wiring lives elsewhere — this page
 * only depends on react-router's hooks.
 */
export const CampaignComposer = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const { preview, previewError, isPreviewLoading, previewAudience, clearPreview } = useCampaignStore();
  const { templates, fetchTemplates } = useEmailTemplateStore();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedListId, setSelectedListId] = useState('');
  const [selector, setSelector] = useState<AttachmentSelector>(EMPTY_SELECTOR);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [imageSetKey, setImageSetKey] = useState('');
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('manual');
  const [scheduleAt, setScheduleAt] = useState('');
  // Snapshot of the audience/template/settings fields as of the last load
  // or successful save — the dirty-guard below diffs the live form against
  // this rather than the (possibly stale) `campaign` object.
  const [savedSnapshot, setSavedSnapshot] = useState('');

  // Every list the viewer can reach (mine + shared, minus system lists) —
  // unfiltered, so a campaign already pointed at an ineligible list can
  // still be looked up for the inline explanation below.
  const [allLists, setAllLists] = useState<List[]>([]);
  const [isListsLoading, setIsListsLoading] = useState(true);
  const [listsError, setListsError] = useState<string | null>(null);
  const [listsReloadToken, setListsReloadToken] = useState(0);

  const [imageSets, setImageSets] = useState<ImageSet[]>([]);
  const [isImageSetsLoading, setIsImageSetsLoading] = useState(true);
  const [imageSetsError, setImageSetsError] = useState<string | null>(null);
  const [imageSetsReloadToken, setImageSetsReloadToken] = useState(0);

  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

  const [runs, setRuns] = useState<CampaignRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<CampaignRun | null>(null);
  const loadRunsRef = useRef<() => Promise<void>>(async () => {});
  // Lets the run-polling effect below (whose deps are just [id], so it
  // doesn't re-run — and doesn't reset its 5s interval — every time
  // `campaign` changes) read the latest campaign status without closing
  // over a stale one from when the effect was set up. Updated synchronously
  // by applyCampaign below, never via a useEffect keyed on `campaign`/
  // `status`: an effect only runs after React commits, and code that reads
  // this ref can run in the same synchronous tick as the setCampaign call
  // that's meant to update it (e.g. runSendNow's refetchCampaign()
  // immediately followed by loadRunsRef.current()) — an effect-based sync
  // would still be reading the pre-update value at that point.
  const campaignStatusRef = useRef<EmailCampaignStatus>('DRAFT');
  // True once the campaign has been fetched at least once. The campaign
  // load and the first runs poll are two independent effects with no
  // ordering guarantee between them — if the runs poll's response lands
  // first and the newest run is already terminal, campaignStatusRef still
  // holds its unloaded default rather than the real (possibly SENDING)
  // status, and skipping the refetch on that basis would leave the page
  // permanently stuck on stale data with no further poll to correct it
  // (a terminal newest run never restarts the polling interval).
  const campaignLoadedRef = useRef(false);
  // Guards against an older in-flight GET /campaigns/:id overwriting a
  // newer one's result: campaignLoadedRef's own corrective refetch (fired
  // from loadRuns when the initial load hasn't landed yet) can resolve
  // *before* that still-pending initial request does, since there's no
  // ordering guarantee between two independent fetches. Without this, the
  // initial request's late, stale response would land last and clobber the
  // corrective one — with the triggering run already terminal, no further
  // poll exists to repair it. Every campaign fetch increments this and
  // captures its own value; a response only gets applied if its captured
  // generation is still the current one when it resolves. Deliberately
  // never reset to 0 (including on an `id` change) — only ever incremented
  // — so a late response from a previous id can never coincide with a
  // fresh request's number and be misapplied.
  const campaignRequestGenRef = useRef(0);

  function applyCampaign(next: Campaign | null) {
    campaignLoadedRef.current = true;
    campaignStatusRef.current = next?.status ?? 'DRAFT';
    setCampaign(next);
  }

  // Load the campaign being edited (or reset to a blank form for /campaigns/new).
  useEffect(() => {
    let cancelled = false;
    // A new id means whatever this page previously knew about a different
    // campaign's status is stale context, not a fact about this one — the
    // generation counter itself is never reset (see its declaration above).
    campaignLoadedRef.current = false;
    campaignStatusRef.current = 'DRAFT';
    async function load() {
      if (!id) {
        campaignRequestGenRef.current += 1; // invalidate any campaign fetch still in flight for the previous id
        applyCampaign(null);
        setName('');
        setDescription('');
        setSelectedListId('');
        setSelector(EMPTY_SELECTOR);
        setSelectedTemplateId('');
        setImageSetKey('');
        setScheduleMode('manual');
        setScheduleAt('');
        setLoadError(null);
        setIsLoadingCampaign(false);
        setSavedSnapshot(formSnapshot({ listId: '', selector: EMPTY_SELECTOR, templateId: '', imageSetKey: '', scheduleMode: 'manual', scheduleAt: '' }));
        return;
      }
      setIsLoadingCampaign(true);
      setLoadError(null);
      const gen = ++campaignRequestGenRef.current;
      try {
        const c = await campaignsApi.get(id);
        // A newer campaign request (e.g. loadRuns' corrective refetch,
        // fired because this request hadn't landed yet) has since started
        // and already applied its own, more current response — this one
        // is stale and must not overwrite it.
        if (cancelled || gen !== campaignRequestGenRef.current) return;
        const nextSelector: AttachmentSelector = {
          blastRoles: c.attachmentSelector.blastRoles ?? [],
          crmContacts: c.attachmentSelector.crmContacts ?? false,
          donors: false,
          purchasers: false,
        };
        const nextScheduleMode: ScheduleMode = c.sendAt ? 'schedule' : 'manual';
        const nextScheduleAt = c.sendAt ? toDatetimeLocalValue(c.sendAt) : '';
        applyCampaign(c);
        setName(c.name);
        setDescription(c.description ?? '');
        setSelectedListId(c.listId);
        setSelector(nextSelector);
        setSelectedTemplateId(c.templateId);
        setImageSetKey(c.imageSetKey ?? '');
        setScheduleMode(nextScheduleMode);
        setScheduleAt(nextScheduleAt);
        setSavedSnapshot(formSnapshot({ listId: c.listId, selector: nextSelector, templateId: c.templateId, imageSetKey: c.imageSetKey ?? '', scheduleMode: nextScheduleMode, scheduleAt: nextScheduleAt }));
      } catch (e) {
        if (!cancelled) setLoadError(extractErrorMessage(e, 'Failed to load campaign'));
      } finally {
        if (!cancelled) setIsLoadingCampaign(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    let cancelled = false;
    async function loadLists() {
      setIsListsLoading(true);
      setListsError(null);
      try {
        // listQuerySchema (packages/backend/src/validators/list.ts) caps
        // limit at 100 — asking for more than that 400s the request.
        const [mine, shared] = await Promise.all([
          listsApi.list('mine', 1, 100),
          listsApi.list('shared', 1, 100),
        ]);
        if (cancelled) return;
        const merged = new Map<string, List>();
        for (const l of [...mine.lists, ...shared.lists]) {
          if (!l.isSystem) merged.set(l.id, l);
        }
        setAllLists(Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name)));
      } catch (e) {
        if (!cancelled) {
          setAllLists([]);
          setListsError(extractErrorMessage(e, "Couldn't load lists"));
        }
      } finally {
        if (!cancelled) setIsListsLoading(false);
      }
    }
    loadLists();
    return () => {
      cancelled = true;
    };
  }, [listsReloadToken]);

  useEffect(() => {
    let cancelled = false;
    async function loadImageSets() {
      setIsImageSetsLoading(true);
      setImageSetsError(null);
      try {
        const sets = await campaignsApi.listImageSets();
        if (!cancelled) setImageSets(sets);
      } catch (e) {
        if (!cancelled) {
          setImageSets([]);
          setImageSetsError(extractErrorMessage(e, "Couldn't load image sets"));
        }
      } finally {
        if (!cancelled) setIsImageSetsLoading(false);
      }
    }
    loadImageSets();
    return () => {
      cancelled = true;
    };
  }, [imageSetsReloadToken]);

  // A campaign audience slices orgs and programs, not people (services/
  // campaigns/audienceResolver.ts sliceAudience, one org- or program-grain
  // anchor per item) — a SMART list of people is rejected outright by the
  // backend, and a MANUAL list holding only PERSON items silently resolves
  // to a zero-org slice. Both are excluded from the picker; kindCounts
  // (only present for MANUAL lists) is what makes the manual case
  // distinguishable without fetching each list individually.
  function isPersonOnlyList(l: List): boolean {
    if (l.kind === 'SMART') return l.smartEntityKind === 'PERSON';
    const kc = l.kindCounts;
    if (!kc) return false;
    return kc.PERSON > 0 && kc.SCHOOL === 0 && kc.CLUB_TEAM === 0 && kc.PROGRAM === 0;
  }

  const pickableLists = useMemo(() => allLists.filter((l) => !isPersonOnlyList(l)), [allLists]);
  const selectedListMeta = useMemo(() => allLists.find((l) => l.id === selectedListId) ?? null, [allLists, selectedListId]);
  const selectedListIsPersonOnly = !!selectedListMeta && isPersonOnlyList(selectedListMeta);

  // The preview is only valid for the audience it was run against — clear it
  // the moment the list or attachment sources change underneath it.
  // The chosen image set changes who is skipped for lack of a Command store,
  // so it invalidates the preview too.
  const selectorKey = `${selector.blastRoles.join(',')}|${selector.crmContacts}`;
  useEffect(() => {
    clearPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListId, selectorKey, imageSetKey]);

  // Runs table for a saved campaign: load on mount, then poll every 5s while
  // any run is still queued or running, stopping the moment none are.
  // loadRunsRef lets the send/test/cancel handlers below trigger the same
  // fetch (and, if it turns up a fresh active run, restart polling for it).
  useEffect(() => {
    if (!id) {
      setRuns([]);
      loadRunsRef.current = async () => {};
      return;
    }
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    async function loadRuns() {
      try {
        const fetched = await campaignsApi.runs(id!);
        if (cancelled) return;
        setRuns(fetched);
        const hasActive = fetched.some((r) => ACTIVE_RUN_STATUSES.includes(r.status));
        if (hasActive && !intervalId) {
          intervalId = setInterval(loadRuns, 5000);
        } else if (!hasActive && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        // The newest run just landed in a terminal state while this page's
        // local campaign is still SENDING/SCHEDULED (or its status isn't
        // known yet at all — see campaignLoadedRef above) — e.g. a ceiling
        // refusal flips the campaign back to DRAFT server-side the moment
        // its run fails, but this poll only fetched `runs`, not `campaign`.
        // Without this, the page keeps showing "actively sending" (and
        // offering Cancel sending) for a send that has already stopped,
        // and the over-limit recovery banner never appears until reload.
        // Gated on !hasActive (not just the newest row's own status) so a
        // newer terminal run sitting in front of an older still-active one
        // (e.g. a completed test send queued after a real send already in
        // flight) doesn't refetch the unchanged campaign on every 5s tick
        // until that other run finishes too.
        const newest = fetched[0];
        const newestIsTerminal = !!newest && !ACTIVE_RUN_STATUSES.includes(newest.status);
        const campaignStatusIsStale =
          !campaignLoadedRef.current || campaignStatusRef.current === 'SENDING' || campaignStatusRef.current === 'SCHEDULED';
        if (newestIsTerminal && !hasActive && campaignStatusIsStale) {
          await refetchCampaign();
        }
      } catch {
        // best-effort; the Runs card just keeps showing whatever it last had
      }
    }

    loadRunsRef.current = loadRuns;
    loadRuns();
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [id]);

  const status: EmailCampaignStatus = campaign?.status ?? 'DRAFT';
  const isLocked = campaign ? LOCKED_STATUSES.includes(campaign.status) : false;
  // A run refused for exceeding the soft recipient ceiling flips the
  // campaign back to DRAFT with nothing left to resume (see
  // services/campaigns/pipeline.ts) — the /resume route then refuses it
  // too, since it only accepts a FAILED/PARTIAL run on a still-SENDING
  // campaign. The newest REAL run (runs is fetched newest-first, but a
  // DRAFT campaign can still queue a TEST send — a later TEST run must not
  // hide an unresolved REAL-run refusal) is the only one worth surfacing
  // here; an older ceiling failure the campaign has since moved past is not.
  const overLimitRun = useMemo(() => {
    if (status !== 'DRAFT') return null;
    const newestReal = runs.find((r) => r.kind === 'REAL');
    if (!newestReal || newestReal.status !== 'FAILED') return null;
    return newestReal.failureCode === 'AUDIENCE_OVER_LIMIT' ? newestReal : null;
  }, [status, runs]);
  const attachmentValid = selector.blastRoles.length > 0 || selector.crmContacts;
  const canSave = !isLocked && !!name.trim() && !!selectedTemplateId && !!selectedListId && attachmentValid;
  const canScheduleSave = canSave && scheduleMode === 'schedule' && !!scheduleAt;
  const selectedTemplate = useMemo(() => templates.find((t) => t.id === selectedTemplateId) ?? null, [templates, selectedTemplateId]);
  // Send now and Send test both dispatch against the persisted campaign, not
  // this form — if the form has unsaved audience/template/settings changes,
  // a send would silently use the stale saved values instead.
  const isDirty = useMemo(
    () => formSnapshot({ listId: selectedListId, selector, templateId: selectedTemplateId, imageSetKey, scheduleMode, scheduleAt }) !== savedSnapshot,
    [selectedListId, selector, selectedTemplateId, imageSetKey, scheduleMode, scheduleAt, savedSnapshot],
  );

  // Mirrors canSave's own checks, in the same order, so a disabled Save
  // button can name the first one the form still fails.
  function saveDisabledReason(): string | null {
    if (!name.trim()) return 'Add a name to save';
    if (!selectedTemplateId) return 'Choose a template to save';
    if (!selectedListId) return 'Choose a list to save';
    if (!attachmentValid) return 'Select at least one audience source to save';
    return null;
  }

  // Mirrors canScheduleSave: everything saveDisabledReason checks, plus the
  // schedule fields it adds on top.
  function scheduleDisabledReason(): string | null {
    const base = saveDisabledReason();
    if (base) return base;
    if (scheduleMode !== 'schedule') return 'Choose "Schedule for…" to set a send time';
    if (!scheduleAt) return 'Choose a date and time to schedule';
    return null;
  }

  function previewDisabledReason(): string | null {
    if (isLocked || isPreviewLoading) return null;
    if (!selectedListId) return 'Choose a list to preview';
    if (!attachmentValid) return 'Select at least one audience source to preview';
    return null;
  }

  // Send test and Send now both act on the persisted campaign; pendingAction
  // is a transient "another action is in flight" state the button's own
  // "Saving…"/"Sending…" label already communicates, so it isn't named here.
  function sendNowDisabledReason(): string | null {
    if (pendingAction !== null) return null;
    if (isDirty) return 'Save your changes first';
    if (!preview) return 'Preview the audience above before sending';
    if (preview.dedupedCount === 0) return 'No recipients matched — adjust the audience before sending';
    return null;
  }

  async function refetchCampaign() {
    if (!id) return;
    const gen = ++campaignRequestGenRef.current;
    try {
      const c = await campaignsApi.get(id);
      // See the generation check in the initial-load effect above — the
      // same ordering hazard applies here in reverse: a slower initial
      // load could still be in flight when this fires, and must not be
      // allowed to overwrite this response once it (eventually) resolves.
      if (gen !== campaignRequestGenRef.current) return;
      applyCampaign(c);
    } catch {
      // best-effort refresh; the triggering action already toasted its own outcome
    }
  }

  function toggleRole(role: BlastRoleKey) {
    setSelector((prev) => {
      const has = prev.blastRoles.includes(role);
      return { ...prev, blastRoles: has ? prev.blastRoles.filter((r) => r !== role) : [...prev.blastRoles, role] };
    });
  }

  function buildBasePayload(): Record<string, unknown> {
    return {
      name: name.trim(),
      description: description.trim(),
      templateId: selectedTemplateId,
      listId: selectedListId,
      imageSetKey: imageSetKey || null,
      attachmentSelector: { blastRoles: selector.blastRoles, crmContacts: selector.crmContacts, donors: false, purchasers: false },
      fromIdentity: 'BLAST_MAIN',
    };
  }

  async function persist(extra: Record<string, unknown>, successMessage: string, action: 'draft' | 'schedule' | 'save') {
    setPendingAction(action);
    try {
      const payload = { ...buildBasePayload(), ...extra };
      if (!id) {
        const created = await campaignsApi.create(payload);
        toast.success(successMessage);
        navigate(`/campaigns/${created.id}`);
      } else {
        const updated = await campaignsApi.update(id, payload);
        applyCampaign(updated);
        setSavedSnapshot(formSnapshot({ listId: selectedListId, selector, templateId: selectedTemplateId, imageSetKey, scheduleMode, scheduleAt }));
        toast.success(successMessage);
      }
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to save campaign'));
    } finally {
      setPendingAction(null);
    }
  }

  function handleSaveDraft() {
    if (!canSave) return;
    persist({ sendAt: null, status: 'DRAFT' }, id ? 'Draft saved' : 'Campaign created', 'draft');
  }

  function handleSaveAndSchedule() {
    if (!canScheduleSave) return;
    const iso = new Date(scheduleAt).toISOString();
    persist({ sendAt: iso, status: 'SCHEDULED' }, id ? 'Campaign scheduled' : 'Campaign created and scheduled', 'schedule');
  }

  function handleSaveChanges() {
    if (!canScheduleSave) return;
    const iso = new Date(scheduleAt).toISOString();
    persist({ sendAt: iso, status: 'SCHEDULED' }, 'Changes saved', 'save');
  }

  async function handleSendTest() {
    if (!id) return;
    setPendingAction('test');
    try {
      const run = await campaignsApi.testSend(id);
      if (run.status === 'SUCCESS') toast.success('Test email sent to you');
      else toast.error('Test send failed to reach Blast');
      await refetchCampaign();
      await loadRunsRef.current();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to send test'));
    } finally {
      setPendingAction(null);
    }
  }

  async function handlePreview() {
    if (!selectedListId || !attachmentValid) return;
    await previewAudience(selectedListId, selector, imageSetKey || null);
  }

  // send-now always returns 202 immediately — the ceiling check happens
  // later, asynchronously, inside the pipeline — so a caller here never
  // sees a synchronous "exceeds" rejection to retry against. confirmRunId
  // (required by the server whenever confirmOverLimit is true) only ever
  // comes from the over-limit banner below, already bound to the specific
  // refused run.
  async function runSendNow(confirmOverLimit: boolean, confirmRunId?: string) {
    if (!id) return;
    try {
      await campaignsApi.sendNow(id, confirmOverLimit, confirmRunId);
      toast.success('Campaign send started');
      setConfirmState(null);
      await refetchCampaign();
      await loadRunsRef.current();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to send campaign'));
      setConfirmState(null);
    }
  }

  function openSendNowConfirm() {
    if (!id || !preview) return;
    const count = preview.dedupedCount;
    setConfirmState({
      title: 'Send campaign now',
      message: `Send this campaign now to ${count} recipient${count === 1 ? '' : 's'}? This can't be undone.`,
      confirmLabel: 'Send now',
      onConfirm: () => runSendNow(false),
    });
  }

  function openOverLimitConfirm() {
    if (!id || !overLimitRun || isDirty) return;
    const count = overLimitRun.dedupedCount ?? 0;
    setConfirmState({
      title: 'Recipient limit exceeded',
      message: `${overLimitRun.errorMessage ?? `Audience of ${count} recipients exceeds the send limit`} Send anyway to everyone who matches?`,
      confirmLabel: 'Send anyway',
      danger: true,
      onConfirm: () => runSendNow(true, overLimitRun.id),
    });
  }

  function openCancelConfirm() {
    if (!id || !campaign) return;
    const isSending = campaign.status === 'SENDING';
    setConfirmState({
      title: isSending ? 'Cancel sending' : 'Cancel schedule',
      message: isSending
        ? 'This campaign is actively sending. Canceling stops any further dispatch immediately.'
        : 'This cancels the scheduled send. The campaign moves to Canceled and can be recreated later.',
      confirmLabel: isSending ? 'Cancel sending' : 'Cancel schedule',
      danger: isSending,
      onConfirm: async () => {
        try {
          await campaignsApi.cancel(id);
          toast.success(isSending ? 'Sending canceled' : 'Schedule canceled');
          setConfirmState(null);
          await refetchCampaign();
          await loadRunsRef.current();
        } catch (e) {
          toast.error(extractErrorMessage(e, 'Failed to cancel'));
          setConfirmState(null);
        }
      },
    });
  }

  // Computed once per render rather than re-invoked at each JSX usage site —
  // saveReason is the base of scheduleReason too, so the helper line below
  // shows each distinct blocking reason once, not the same one twice.
  const saveReason = saveDisabledReason();
  const scheduleReason = scheduleDisabledReason();
  const sendReason = !isNew ? sendNowDisabledReason() : null;
  const previewReason = previewDisabledReason();
  const blockingReasons = Array.from(new Set([saveReason, scheduleReason, sendReason].filter((r): r is string => !!r)));

  if (isLoadingCampaign) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card p-6 text-center space-y-3">
          <p className="text-red-600 dark:text-red-400">{loadError}</p>
          <Link to="/campaigns" className="btn-secondary inline-flex">Back to campaigns</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="p-2 bg-blast-100 dark:bg-blast-900/30 rounded-lg flex-shrink-0">
              <Send className="h-6 w-6 text-blast-600 dark:text-blast-400" />
            </div>
            <div className="min-w-0 flex-1">
              <input
                type="text"
                aria-label="Campaign name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Untitled campaign"
                disabled={isLocked}
                className="text-2xl font-bold text-gray-900 dark:text-gray-100 bg-transparent border-none focus:outline-none focus:ring-0 px-0 w-full disabled:opacity-70"
              />
              <div className="mt-1">
                <CampaignStatusChip status={status} />
              </div>
            </div>
          </div>
          <Link to="/campaigns" className="btn-secondary whitespace-nowrap flex-shrink-0">Back to campaigns</Link>
        </div>

        <div>
          <label className="label mb-1">Description (internal)</label>
          <textarea
            className="input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional internal note about this campaign"
            disabled={isLocked}
          />
        </div>

        {/* 1. Audience */}
        <div className="card p-4 sm:p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Audience</h2>

          <div>
            <label className="label mb-1">List</label>
            {listsError ? (
              <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2">
                <p className="text-xs text-red-700 dark:text-red-400">Couldn't load lists: {listsError}</p>
                <button
                  type="button"
                  className="text-xs font-medium text-blast-600 dark:text-blast-400 hover:underline flex-shrink-0"
                  onClick={() => setListsReloadToken((n) => n + 1)}
                >
                  Retry
                </button>
              </div>
            ) : (
              <select
                className="input"
                value={selectedListId}
                onChange={(e) => setSelectedListId(e.target.value)}
                disabled={isLocked || isListsLoading}
              >
                <option value="">{isListsLoading ? 'Loading lists…' : 'Select a list…'}</option>
                {selectedListIsPersonOnly && selectedListMeta && (
                  <option value={selectedListMeta.id} disabled>
                    {selectedListMeta.name} (people, not usable)
                  </option>
                )}
                {pickableLists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}{l.kind === 'SMART' ? ' (smart)' : ''}
                  </option>
                ))}
              </select>
            )}
            {selectedListIsPersonOnly && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                This list contains people, not organizations, so it can't be used as a campaign audience.
              </p>
            )}
            {!isListsLoading && !listsError && pickableLists.length === 0 && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">No eligible lists found — build one in Explore first.</p>
            )}
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              <Link to="/explore" className="text-blast-600 dark:text-blast-400 hover:underline">Manage lists in Explore →</Link>
            </p>
          </div>

          <div>
            <p className="label mb-2">Include</p>
            <AudienceRoleGroups
              selected={selector.blastRoles}
              crmContacts={selector.crmContacts}
              disabled={isLocked}
              onToggleRole={toggleRole}
              onToggleCrmContacts={() => setSelector((prev) => ({ ...prev, crmContacts: !prev.crmContacts }))}
            />
            <div className="space-y-2 mt-2">
              <label className="flex items-center gap-2 opacity-60">
                <input type="checkbox" checked={false} disabled className="rounded border-gray-300" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Donors <span className="text-xs text-gray-400 dark:text-gray-500">(coming with their data feeds)</span>
                </span>
              </label>
              <label className="flex items-center gap-2 opacity-60">
                <input type="checkbox" checked={false} disabled className="rounded border-gray-300" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Gear purchasers <span className="text-xs text-gray-400 dark:text-gray-500">(coming with their data feeds)</span>
                </span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              className="btn-secondary"
              onClick={handlePreview}
              disabled={isLocked || !selectedListId || !attachmentValid || isPreviewLoading}
              title={previewReason ?? undefined}
            >
              {isPreviewLoading ? 'Loading preview…' : 'Preview audience'}
            </button>
            {previewReason && <p className="text-xs text-gray-400 dark:text-gray-500">{previewReason}</p>}
          </div>

          {previewError && (
            <p role="alert" className="text-xs text-red-600 dark:text-red-400">{previewError}</p>
          )}

          {preview && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">List slice</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{preview.sliceCount}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Resolved</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{preview.resolvedCount}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Deduped</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{preview.dedupedCount}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Do-not-email</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{preview.doNotEmailCount}</p>
                </div>
              </div>
              {!!preview.noCommandStoreCount && preview.noCommandStoreCount > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {preview.noCommandStoreCount} recipient{preview.noCommandStoreCount === 1 ? ' has' : 's have'} no Command store and will be skipped
                </p>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-2 py-1 text-left">Email</th>
                      <th className="px-2 py-1 text-left">Source</th>
                      <th className="px-2 py-1 text-left">Role</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-900 dark:text-gray-100">
                    {preview.sample.length === 0 ? (
                      <tr><td colSpan={3} className="px-2 py-3 text-center text-gray-400 dark:text-gray-500">No recipients matched</td></tr>
                    ) : preview.sample.map((r, i) => (
                      <tr key={`${r.email}-${i}`} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="px-2 py-1 whitespace-nowrap">{r.email}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{r.source}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{r.roleDetail ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* 2. Template */}
        <div className="card p-4 sm:p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Template</h2>
          <select
            className="input"
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            disabled={isLocked}
          >
            <option value="">Select a template…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {templates.length === 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              No active templates yet. <Link to="/campaigns/templates" className="text-blast-600 dark:text-blast-400 hover:underline">Create one →</Link>
            </p>
          )}
          <ImageSetPicker
            value={imageSetKey}
            onChange={setImageSetKey}
            imageSets={imageSets}
            isLoading={isImageSetsLoading}
            error={imageSetsError}
            onRetry={() => setImageSetsReloadToken((n) => n + 1)}
            disabled={isLocked}
            templateImageUrl={selectedTemplate?.slots.imageUrl}
            hasTemplate={!!selectedTemplate}
          />
          {selectedTemplate && <TemplatePreview subject={selectedTemplate.subject} slots={selectedTemplate.slots} />}
        </div>

        {/* 3. Settings */}
        <div className="card p-4 sm:p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Settings</h2>

          <div>
            <label className="label mb-1">Send from</label>
            <select className="input" value="BLAST_MAIN" disabled={isLocked} onChange={() => { /* only BLAST_MAIN is selectable today */ }}>
              <option value="BLAST_MAIN">Blast Athletics (main)</option>
              <option value="OUTREACH" disabled>Outreach (pending Blast domain setup)</option>
            </select>
          </div>

          <div>
            <p className="label mb-2">Schedule</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="schedule-mode"
                  className="text-blast-600 focus:ring-blast-500"
                  checked={scheduleMode === 'manual'}
                  onChange={() => setScheduleMode('manual')}
                  disabled={isLocked}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Send manually</span>
              </label>
              <label className="flex items-center gap-2 flex-wrap">
                <input
                  type="radio"
                  name="schedule-mode"
                  className="text-blast-600 focus:ring-blast-500"
                  checked={scheduleMode === 'schedule'}
                  onChange={() => setScheduleMode('schedule')}
                  disabled={isLocked}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Schedule for…</span>
                <input
                  type="datetime-local"
                  className="input w-auto"
                  value={scheduleAt}
                  onChange={(e) => setScheduleAt(e.target.value)}
                  disabled={isLocked || scheduleMode !== 'schedule'}
                />
              </label>
            </div>
          </div>
        </div>

        {/* 4. Review & actions */}
        <div className="card p-4 sm:p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review & actions</h2>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-gray-400 dark:text-gray-500">Audience</dt>
              <dd className="text-gray-900 dark:text-gray-100">{summarizeSelector(selector)}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400 dark:text-gray-500">Template</dt>
              <dd className="text-gray-900 dark:text-gray-100">{selectedTemplate?.name ?? 'Not selected'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400 dark:text-gray-500">From</dt>
              <dd className="text-gray-900 dark:text-gray-100">Blast Athletics (main)</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400 dark:text-gray-500">Schedule</dt>
              <dd className="text-gray-900 dark:text-gray-100">
                {scheduleMode === 'schedule' && scheduleAt ? new Date(scheduleAt).toLocaleString() : 'Manual'}
              </dd>
            </div>
          </dl>

          {overLimitRun && (
            <div className="flex flex-col gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-red-800 dark:text-red-300">{overLimitRun.errorMessage}</p>
                <button
                  type="button"
                  className="btn-primary whitespace-nowrap"
                  onClick={openOverLimitConfirm}
                  disabled={isDirty}
                  title={isDirty ? 'Save your changes first' : undefined}
                >
                  Send anyway
                </button>
              </div>
              {isDirty && (
                <p className="text-xs text-red-700 dark:text-red-400">
                  Save your changes first before sending anyway — this audience must match what was refused.
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
            {isNew || status === 'DRAFT' ? (
              <>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleSaveDraft}
                  disabled={!canSave || pendingAction !== null}
                  title={saveReason ?? undefined}
                >
                  {pendingAction === 'draft' ? 'Saving…' : 'Save draft'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleSaveAndSchedule}
                  disabled={!canScheduleSave || pendingAction !== null}
                  title={scheduleReason ?? undefined}
                >
                  {pendingAction === 'schedule' ? 'Saving…' : 'Save & schedule'}
                </button>
                {!isNew && (
                  <>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleSendTest}
                      disabled={pendingAction !== null || isDirty}
                      title={isDirty ? 'Save your changes first' : undefined}
                    >
                      {pendingAction === 'test' ? 'Sending…' : 'Send test to me'}
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={openSendNowConfirm}
                      disabled={!preview || preview.dedupedCount === 0 || pendingAction !== null || isDirty}
                      title={sendReason ?? undefined}
                    >
                      Send now
                    </button>
                  </>
                )}
                {pendingAction === null && blockingReasons.length > 0 && (
                  <div className="w-full space-y-0.5">
                    {blockingReasons.map((reason) => (
                      <p key={reason} className="text-xs text-amber-700 dark:text-amber-400">{reason}</p>
                    ))}
                  </div>
                )}
              </>
            ) : status === 'SCHEDULED' ? (
              <>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSaveChanges}
                  disabled={!canScheduleSave || pendingAction !== null}
                  title={scheduleReason ?? undefined}
                >
                  {pendingAction === 'save' ? 'Saving…' : 'Save changes'}
                </button>
                <button type="button" className="btn-secondary" onClick={openCancelConfirm}>Cancel schedule</button>
                {pendingAction === null && scheduleReason && (
                  <p className="w-full text-xs text-amber-700 dark:text-amber-400">{scheduleReason}</p>
                )}
              </>
            ) : status === 'SENDING' ? (
              <button type="button" className="btn-danger" onClick={openCancelConfirm}>Cancel sending</button>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This campaign is {status.toLowerCase()} and can no longer be edited.
              </p>
            )}
          </div>
        </div>

        {/* 5. Run history — only meaningful once the campaign has been saved */}
        {!isNew && (
          <div className="card p-4 sm:p-5 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Runs</h2>
            {runs.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No runs yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-2 py-1 text-left">Kind</th>
                      <th className="px-2 py-1 text-left">Status</th>
                      <th className="px-2 py-1 text-left">Accepted</th>
                      <th className="px-2 py-1 text-left">Deduped</th>
                      <th className="px-2 py-1 text-left">Started</th>
                      <th className="px-2 py-1 text-left">Trigger</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-900 dark:text-gray-100">
                    {runs.map((run) => (
                      <tr
                        key={run.id}
                        className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                        onClick={() => setSelectedRun(run)}
                      >
                        <td className="px-2 py-1 whitespace-nowrap">{run.kind}</td>
                        <td className="px-2 py-1"><CampaignStatusChip status={run.status} /></td>
                        <td className="px-2 py-1 whitespace-nowrap">{run.acceptedCount ?? '—'}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{run.dedupedCount ?? '—'}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{run.startedAt ?? '—'}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{run.triggerType ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmState !== null}
        title={confirmState?.title ?? ''}
        message={confirmState?.message ?? ''}
        confirmLabel={confirmState?.confirmLabel}
        danger={confirmState?.danger}
        onConfirm={() => confirmState?.onConfirm()}
        onCancel={() => setConfirmState(null)}
      />

      {id && selectedRun && (
        <RunDetailModal
          campaignId={id}
          run={selectedRun}
          onClose={() => {
            setSelectedRun(null);
            loadRunsRef.current();
          }}
        />
      )}
    </div>
  );
};
